import {
  AssetRoutine,
  AssetRoutineState,
  cleanUpDataChanges,
  Issue,
  IssueCreationSource,
  LocalCaptureContext,
  newGUID,
  OfflineMediaRef,
  RoutineQuestionConfig,
  RoutineType,
  SAVE_ROUTINE_DRAFT,
  useCreateMutation,
  useCurrentAccount,
  useCurrentLocation,
  useCustomMutation,
  useIntegrationManagedRoutines,
  useOfflineSubmit,
  useOnlineStatus,
  WorkflowPageConfig,
  WorkflowQuestionType,
  WorkspaceAsset,
  WorkspaceRoutine,
} from '@gripp/shared-logic';
import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Platform, View } from 'react-native';
import Toast from 'react-native-toast-message';

import { Spacing } from 'libs/shared-ui/src/styles';
import { Colors } from '../../../themes/colors';
import { Button } from '../../crop-circle/Button';
import { ArrowLeftIcon } from '../../crop-circle/icons';
import { OfflineSubmitNotice } from '../../offline/OfflineSubmitNotice';
import { tokens } from '../../crop-circle/tokens';
import CustomAlert from '../../customAlert';
import { FormContext } from '../../forms';
import { KeyboardAwareScrollView } from '../../keyboard/keyboardAwareScrollView';
import {
  BottomComponent,
  TopComponent,
  TopComponentCropCircle,
} from '../../navigation';
import { RoutineExecutionForm, RoutineModule } from '../types';
import { themedStyleSheet } from '../../../themes/themedStyleSheet';

export type RoutineFormProps = {
  workspaceAsset: WorkspaceAsset;
  module: RoutineModule<any, any>;
  routine?: WorkspaceRoutine;
  state?: AssetRoutineState<any>;
  additionalState?: any;
  /**
   * Runs once the routine is accepted: sent, or (`queued`) durably held for
   * the offline outbox to send.
   */
  onCompleted: (
    origin?: string,
    assetRoutineId?: string,
    queued?: boolean
  ) => Promise<void>;
  onCancel?: () => void;
  sourceIssue?: Issue;
  topBar: TopComponent;
  topBarCropCircle?: TopComponentCropCircle;
  rightButton?: ReactNode;
  preview?: boolean;
  footer?: ReactNode;
  createdFrom?: IssueCreationSource;
  isIssueEditRoutine?: boolean;
  assetRoutine?: AssetRoutine;
  onSaveDraft?: () => void;
  showSaveDraftButton?: boolean;
  draftData?: any;
  /** The server draft the form was opened from; once the routine is sent only that draft is deleted. */
  loadedDraft?: { id: string; updatedAt?: string | null } | null;
  showNativeModal?: boolean;
  showConfirmBack?: boolean;
  bottomBar?: BottomComponent;
  /**
   * Called on mount with a guard function. Call that function with an `onProceed` callback
   * to check if the form has unsaved changes — if so, the confirm alert is shown and
   * `onProceed` is invoked only when the user confirms; otherwise `onProceed` is called immediately.
   */
  onRegisterBackGuard?: (
    guard: (onProceed: () => void, onCancel?: () => void) => void
  ) => void;
};

// GraphQL mutation will be implemented when backend is ready

export const RoutineForm = (props: RoutineFormProps) => {
  const { t } = useTranslation();
  const { isOnline } = useOnlineStatus();
  const [isProcessing, toggleProcessing] = useState(false);
  const isProcessingRef = useRef(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const pendingProceedRef = useRef<(() => void) | null>(null);
  const pendingCancelRef = useRef<(() => void) | null>(null);
  const [isFromDraft, setIsFromDraft] = useState(false);
  const [keyboardHeight, setKeyboardHeight] = useState(0);
  const { submit, submitMedia, canQueue } = useOfflineSubmit();
  // Photos and signatures go to the device only where the outbox will upload
  // them; an issue revision still updates directly and has no queue to wait in.
  const captureLocally =
    canQueue && !props.isIssueEditRoutine && !props.preview;
  const capturedMedia = useRef(new Map<string, OfflineMediaRef>());
  const localCapture = useMemo(
    () => ({
      register: (media: OfflineMediaRef) => {
        capturedMedia.current.set(media.id, media);
      },
    }),
    []
  );
  // Only photos still in the answers are uploaded: one removed from the form
  // after capture is left for the boot sweep.
  const mediaReferencedBy = (data: unknown): OfflineMediaRef[] => {
    const answers = JSON.stringify(data);
    return [...capturedMedia.current.values()].filter((item) =>
      answers.includes(item.id)
    );
  };
  const { mutateAsync: updateAsync, isPending: isUpdating } = useCreateMutation(
    {
      query: UPDATE_ASSET_ROUTINE,
      modelName: 'assetRoutine',
    }
  );
  const { mutateAsync: saveDraftAsync } = useCustomMutation({
    query: SAVE_ROUTINE_DRAFT,
  });
  const { getLocation } = useCurrentLocation();
  const { account } = useCurrentAccount();
  const { integrationManagedRoutines } = useIntegrationManagedRoutines(
    props.workspaceAsset?.id
  );

  const ExecutionForm: RoutineExecutionForm<any, any> | undefined = useMemo(
    () => props.module.executionForm,
    [props.module.executionForm]
  );
  const formOptions = useMemo(() => {
    const options: any = {
      mode: 'onChange',
    };
    const formResolver =
      props.module.getExecutionFormResolver &&
      props.module.getExecutionFormResolver(props.routine?.config, props.state);

    // Use draft data if available, otherwise use default values
    let defaultFormData;
    if (props.draftData && !props.isIssueEditRoutine) {
      defaultFormData = props.draftData;
    } else {
      defaultFormData =
        props.module.getExecutionFormDefaultValues &&
        props.module.getExecutionFormDefaultValues(
          props.routine?.config,
          props.state
        );
    }

    if (formResolver) {
      options.resolver = formResolver;
    }
    if (defaultFormData) {
      options.defaultValues = defaultFormData;
    }

    return options;
  }, [
    props.module,
    props.routine?.config,
    props.state,
    props.draftData,
    props.isIssueEditRoutine,
  ]);

  const formMethods = useForm(formOptions);

  // Register the back guard once on mount so external callers can intercept back actions
  useEffect(() => {
    props.onRegisterBackGuard?.((onProceed, onCancel) => {
      if (formMethods.formState.isDirty) {
        pendingProceedRef.current = onProceed;
        pendingCancelRef.current = onCancel ?? null;
        setConfirmCancel(true);
      } else {
        onProceed();
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reset form when draft data is loaded
  useEffect(() => {
    if (props.draftData && !props.isIssueEditRoutine) {
      setIsFromDraft(true);
      // Use setTimeout to ensure form is fully mounted before resetting
      setTimeout(() => {
        formMethods.reset(props.draftData, {
          keepDefaultValues: false,
        });

        // Manually set dirty state on all fields
        Object.keys(props.draftData).forEach((key) => {
          formMethods.setValue(key, props.draftData[key], {
            shouldDirty: true,
            shouldValidate: false,
          });
        });
      }, 100);
    }
  }, [props.draftData, formMethods, props.isIssueEditRoutine]);

  const onSaveDraft = async (data: any) => {
    if (isProcessingRef.current) return;
    isProcessingRef.current = true;

    toggleProcessing(true);
    try {
      // The draft lives on the server, outside the outbox, so the outbox takes
      // its local photos first; otherwise nothing uploads them and the boot
      // sweep deletes the files the draft points at. Read from the registry,
      // not `captureLocally`, which can change after the photos were taken.
      const media = mediaReferencedBy(data);
      if (media.length > 0) {
        await submitMedia(media, props.workspaceAsset.workspace.id);
      }
      const result = await saveDraftAsync({
        input: {
          workspaceAssetId: props.workspaceAsset.id,
          routineId: props.routine?.id,
          data: data,
        },
      });

      if (result?.routine?.saveRoutineDraft?.success) {
        // Call the parent's onSaveDraft callback if provided (don't await - let it run async)
        if (props.onSaveDraft) {
          props.onSaveDraft(); // Call without await so it doesn't block
        }
        // Close the form after saving draft
        props.onCancel && props.onCancel();
      } else {
        console.error(
          'Failed to save draft:',
          result?.routine?.saveRoutineDraft?.message
        );
      }
    } catch (error) {
      console.error('Error saving draft:', error);
    } finally {
      isProcessingRef.current = false;
      toggleProcessing(false);
    }
  };

  const onSubmit = async (data: any) => {
    if (isProcessingRef.current) return;
    isProcessingRef.current = true;

    toggleProcessing(true);

    if (props.module.transformSubmitData) {
      data = props.module.transformSubmitData(data);
    }

    if (props?.isIssueEditRoutine) {
      const cleanUpData = cleanUpDataChanges(
        props.assetRoutine?.data,
        data,
        props.routine?.type || RoutineType.Workflow,
        account?.id || ''
      );

      const input: any = {
        id: props.assetRoutine?.id,
        data: {
          ...cleanUpData,
        },
        updatedAt: new Date(),
      };

      if (props.sourceIssue) input.sourceIssue = { id: props.sourceIssue.id };

      await updateAsync(
        { input },
        {
          onSettled: async () => {
            try {
              await props.onCompleted('issue-edit');
            } finally {
              isProcessingRef.current = false;
              toggleProcessing(false);
            }
          },
        }
      );
      return;
    }

    const input: any = {
      id: newGUID(),
      asset: { id: props.workspaceAsset.asset.id },
      workspaceAsset: { id: props.workspaceAsset.id },
      workspace: { id: props.workspaceAsset.workspace.id },
      routine: { id: props.routine?.id },
      routineVersion: { id: props.routine?.currentVersion?.id },
      data: data,
      location: await getLocation(),
      ...(props.createdFrom ? { createdFrom: props.createdFrom } : {}),
    };
    if (props.sourceIssue) input.sourceIssue = { id: props.sourceIssue.id };

    const finish = async (queued: boolean) => {
      try {
        await props.onCompleted(undefined, input.id, queued);
      } finally {
        isProcessingRef.current = false;
        toggleProcessing(false);
      }
    };
    if (!captureLocally) {
      // Sent directly, as before the outbox: the form completes whether or not
      // the server accepted it, and a rejection surfaces through the mutation.
      try {
        await submit('createAssetRoutine', {
          input,
          capturedAt: new Date(),
          loadedDraft: props.loadedDraft,
        });
      } finally {
        await finish(false);
      }
      return;
    }

    let result: Awaited<ReturnType<typeof submit>>;
    try {
      result = await submit('createAssetRoutine', {
        input,
        capturedAt: new Date(),
        loadedDraft: props.loadedDraft,
        media: mediaReferencedBy(data),
      });
    } catch (error) {
      // Nothing was queued, so the form stays open with its answers intact.
      isProcessingRef.current = false;
      toggleProcessing(false);
      console.error('Error submitting routine:', error);
      Toast.show({ type: 'error', text1: t('routines.submitError') });
      return;
    }
    await finish(result.rowId !== null);
  };
  const { isDirty, isValid } = formMethods.formState;

  // The prefill deliberately doesn't dirty the form, so the unsaved-changes
  // guard stays quiet. Without this, a workflow whose only answerable question
  // is a managed counter could never be saved.
  const hasIntegrationManagedPrefill = useMemo(() => {
    if (!integrationManagedRoutines.size) return false;
    const pages: unknown = props.routine?.config?.pages;
    if (!Array.isArray(pages)) return false;
    return (pages as WorkflowPageConfig[]).some((page) =>
      (page?.questions ?? []).some((question) => {
        if (question?.type !== WorkflowQuestionType.Routine) return false;
        const { workspaceRoutineId } = question as RoutineQuestionConfig;
        return (
          !!workspaceRoutineId &&
          integrationManagedRoutines.has(workspaceRoutineId)
        );
      })
    );
  }, [integrationManagedRoutines, props.routine?.config]);

  const canSave =
    isValid === false ||
    (!isFromDraft && isDirty === false && !hasIntegrationManagedPrefill) ||
    isUpdating === true ||
    isProcessing === true ||
    (!isOnline && !canQueue);

  const isWeb = Platform.OS === 'web';
  const showSaveDraft =
    !props.preview && props.showSaveDraftButton && !props.isIssueEditRoutine;
  const submitLabel = props?.isIssueEditRoutine
    ? t('routines.resubmit')
    : t('routines.submit');

  const handleBackPress = () => {
    if (props.showConfirmBack && formMethods.formState.isDirty) {
      setConfirmCancel(true);
    } else {
      props.onCancel?.();
    }
  };

  const BottomBar = props.bottomBar;

  return (
    <View style={styles.routineFormContainer}>
      <FormContext.Provider value={formMethods}>
        {props.topBarCropCircle && (
          <props.topBarCropCircle
            title={props.routine?.config?.routineButtonText}
            rightNode={props.rightButton}
          />
        )}
        {ExecutionForm && (
          <KeyboardAwareScrollView
            containerStyle={styles.container}
            disableKeyboardAvoiding={Platform.OS === 'ios'}
            keyboardShouldPersistTaps="handled"
            showsVerticalScrollIndicator={false}
            style={styles.scrollviewContainer}
            contentContainerStyle={styles.scrollContent}
            keyboardVerticalOffset={Platform.OS === 'android' ? 40 : undefined}
          >
            <View
              style={[
                styles.executionWrapper,
                !isWeb && styles.executionWrapperMobile,
              ]}
            >
              <LocalCaptureContext.Provider
                value={captureLocally ? localCapture : undefined}
              >
                <ExecutionForm
                  config={props.routine?.config}
                  currentState={props.state}
                  workspaceAsset={props.workspaceAsset}
                  toggleProcessing={toggleProcessing}
                  isIssueEditRoutine={props.isIssueEditRoutine}
                  workspaceRoutineId={props.routine?.id}
                  captureLocally={captureLocally}
                />
              </LocalCaptureContext.Provider>
            </View>
            {!canQueue && <OfflineSubmitNotice />}
            {BottomBar ? (
              <BottomBar
                onSave={formMethods.handleSubmit(onSubmit)}
                onCancel={handleBackPress}
                showConfirmBack={props.showConfirmBack}
                confirmBack={isDirty}
                saveLabel={submitLabel}
                rightPressDisabled={canSave}
              />
            ) : (
              <View
                style={[
                  styles.actionsColumn,
                  !isWeb && styles.actionsColumnFabSpacer,
                ]}
              >
                <Button
                  variant="rectangular"
                  size="large"
                  state={canSave ? 'default' : 'active'}
                  disabled={canSave}
                  onPress={formMethods.handleSubmit(onSubmit)}
                  style={styles.webSubmitButton}
                >
                  {submitLabel}
                </Button>
                {showSaveDraft && (
                  <Button
                    variant="rectangular"
                    size="large"
                    state="default"
                    style={styles.saveDraftButton}
                    onPress={() => {
                      const formData = formMethods.getValues();
                      onSaveDraft(formData);
                    }}
                    disabled={!isDirty || isProcessing || !isOnline}
                  >
                    {t('routines.saveDraft')}
                  </Button>
                )}
              </View>
            )}
          </KeyboardAwareScrollView>
        )}
        {!isWeb && !BottomBar && (
          <Button
            variant="rounded"
            state="active"
            onPress={handleBackPress}
            style={styles.floatingBackButton}
            leftIcon={
              <ArrowLeftIcon color={Colors.role.textOnInverse} size={24} />
            }
            accessibilityLabel={t('topNav.back')}
          />
        )}
      </FormContext.Provider>
      <CustomAlert
        visible={confirmCancel}
        primaryButtonLabel={t('routines.modalCancel.primary')}
        secondaryButtonLabel={t('routines.modalCancel.secondary')}
        tertiaryButtonLabel={
          props.showSaveDraftButton && isOnline
            ? t('topNav.customAlert.saveProgress')
            : undefined
        }
        action={() => {
          const proceed = pendingProceedRef.current;
          pendingProceedRef.current = null;
          if (proceed) {
            proceed();
          } else {
            props.onCancel && props.onCancel();
          }
        }}
        tertiaryAction={async () => {
          const formData = formMethods.getValues();
          await onSaveDraft(formData);
        }}
        hideAlert={() => {
          const cancel = pendingCancelRef.current;
          pendingProceedRef.current = null;
          pendingCancelRef.current = null;
          setConfirmCancel(false);
          cancel?.();
        }}
        inline
      />
    </View>
  );
};

const UPDATE_ASSET_ROUTINE = `
  mutation UpdateAssetRoutine($input: MutateAssetRoutineInput!) {
    updateAssetRoutine(input: $input) {
      id
      data
      updatedAt
    }
  }
`;

const styles = themedStyleSheet((role) => ({
  container: {
    flex: 1,
    flexDirection: 'column',
    backgroundColor: role.canvas,
  },
  rowCropCircle: {
    justifyContent: 'flex-start',
    marginBottom: 80,
  },
  scrollviewContainer: {
    flex: 1,
  },
  actionsColumn: {
    flexDirection: 'column',
    alignItems: 'stretch',
    marginTop: tokens.spacing.lg,
    gap: tokens.spacing.xs,
  },
  actionsColumnFabSpacer: {
    marginBottom: tokens.spacing['8xl'],
  },
  scrollContent: {
    flexGrow: 1,
    padding: Spacing.smallPadding.paddingHorizontal,
    paddingBottom: 16,
  },
  executionWrapper: {
    flexGrow: 1,
  },
  // On mobile (native), let the content take its natural height so the submit
  // button trails the end of the routine content instead of being pushed to the
  // bottom of the viewport — where an open keyboard can hide it on short forms
  // (counters, status, short mini-forms). Desktop keeps flexGrow: 1 so the
  // button stays pinned to the bottom of the panel.
  executionWrapperMobile: {
    flexGrow: 0,
  },
  webSubmitButton: {
    width: '100%',
  },
  saveDraftButton: {
    width: '100%',
    backgroundColor: Colors.secondaryContainer,
  },
  routineFormContainer: {
    flex: 1,
    position: 'relative',
    backgroundColor: role.canvas,
  },
  floatingBackButton: {
    ...tokens.shadows.md,
    position: 'absolute',
    bottom: tokens.spacing['2xl'],
    left: tokens.spacing.xl,
    width: tokens.spacing['7xl'],
    height: tokens.spacing['7xl'],
    minWidth: tokens.spacing['7xl'],
    maxHeight: tokens.spacing['7xl'],
    borderRadius: tokens.borderRadius.pill,
    zIndex: 10,
  },
}));
